<template>
  <div class="return-quality-inspection">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>退货质检工作台</span>
        <div>
          <span class="header-tip">扫描退回运单号加载原单</span>
          <el-button
            class="record-link"
            type="text"
            @click="$router.push({ name: 'returnQualityInspectionRecords' })"
          >
            查看质检记录
          </el-button>
        </div>
      </div>
      <el-form inline @submit.native.prevent>
        <el-form-item label="退回运单号">
          <el-input
            ref="returnMailNo"
            v-model.trim="returnMailNo"
            clearable
            placeholder="请扫描或输入退回运单号"
            style="width: 360px"
            @keyup.enter.native="queryInspection"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="querying"
            :disabled="!ownerReady"
            @click="queryInspection"
          >
            查询原单
          </el-button>
          <el-button @click="resetWorkbench">清空</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <template v-if="inspection">
      <el-alert
        v-if="inspection.relationStatus === 'UNRELATED'"
        class="section-card"
        title="该退回运单号未关联原出库单，只能登记拒单。"
        type="warning"
        show-icon
        :closable="false"
      />

      <el-card class="section-card" shadow="never">
        <div slot="header" class="card-header">
          <span>质检信息</span>
          <el-tag :type="statusTagType">{{ statusText }}</el-tag>
        </div>
        <el-row :gutter="24" class="info-row">
          <el-col :span="8">
            <span class="info-label">退回运单号</span>
            {{ inspection.returnMailNo || "-" }}
          </el-col>
          <el-col :span="10">
            <span class="info-label">渠道单号</span>
            {{ inspection.originMerchantOrderNo || "-" }}
          </el-col>
          <el-col :span="6">
            <span class="info-label">货主</span>
            {{ inspection.customerCode || "-" }}
          </el-col>
        </el-row>
        <el-row v-if="inspection.finalized" :gutter="24" class="info-row">
          <el-col :span="8">
            <span class="info-label">完成时间</span>
            {{ formatTime(inspection.completedAt) }}
          </el-col>
          <el-col
            :span="8"
            v-if="inspection.finalized && inspection.rejectionReason"
          >
            <span class="info-label">拒单原因</span>
            {{ inspection.rejectionReason }}
          </el-col>
        </el-row>
      </el-card>

      <el-card
        v-if="canEdit && !inspection.finalized"
        class="section-card"
        shadow="never"
      >
        <div slot="header">扫码质检</div>
        <div class="scan-bar">
          <el-input
            ref="scanBarcode"
            v-model.trim="scanBarcode"
            clearable
            placeholder="请扫描实物条码，回车后数量加 1"
            @keyup.enter.native="addScan"
          />
          <el-button @click="clearScans">清空扫描</el-button>
        </div>

        <el-table
          v-if="barcodeProgress.length"
          :data="barcodeProgress"
          border
          size="small"
        >
          <el-table-column prop="skuName" label="产品名称" min-width="180" />
          <el-table-column
            prop="physicalBarcode"
            label="实物条码"
            min-width="220"
          />
          <el-table-column
            prop="expectedQuantity"
            label="应检数量"
            width="120"
            align="right"
          />
          <el-table-column
            prop="scannedQuantity"
            label="已扫数量"
            width="120"
            align="right"
          />
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag
                :type="
                  scope.row.unexpected
                    ? 'danger'
                    : scope.row.scannedQuantity > scope.row.expectedQuantity
                    ? 'danger'
                    : scope.row.scannedQuantity === scope.row.expectedQuantity
                    ? 'success'
                    : 'warning'
                "
                size="small"
              >
                {{
                  scope.row.unexpected
                    ? "非本单"
                    : scope.row.scannedQuantity > scope.row.expectedQuantity
                    ? "超扫"
                    : scope.row.scannedQuantity === scope.row.expectedQuantity
                    ? "已完成"
                    : "待扫描"
                }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" align="center">
            <template slot-scope="scope">
              <el-button
                type="text"
                :disabled="scope.row.scannedQuantity <= 0"
                @click="decreaseScan(scope.row.physicalBarcode)"
              >
                撤销一次
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="action-bar">
          <span v-if="hasMissingBarcode" class="danger-tip">
            原单存在未配置实物条码的商品，不能完成质检。
          </span>
          <span v-else-if="hasUnexpectedScan" class="danger-tip">
            存在非本单商品，只能拒单。
          </span>
          <span v-else-if="hasOverScan" class="danger-tip">
            存在超扫商品，只能拒单。
          </span>
          <span
            v-else-if="inspection.relationStatus === 'UNRELATED'"
            class="action-tip"
          >
            可扫描退货商品后登记拒单。
          </span>
          <span v-else-if="!canComplete" class="action-tip">
            完成质检前，各实物条码的扫描数量必须与应检数量一致。
          </span>
          <el-button
            type="primary"
            :disabled="!canComplete"
            :loading="submitting"
            @click="submitCompletion"
          >
            完成质检
          </el-button>
          <el-button type="danger" plain @click="openRejection">
            拒单
          </el-button>
        </div>
      </el-card>

      <el-card
        v-if="(inspection.finalized || !canEdit) && barcodeProgress.length"
        class="section-card"
        shadow="never"
      >
        <div slot="header">扫描汇总</div>
        <el-table :data="barcodeProgress" border size="small">
          <el-table-column
            prop="physicalBarcode"
            label="实物条码"
            min-width="220"
          />
          <el-table-column prop="skuName" label="产品名称" min-width="180" />
          <el-table-column
            prop="expectedQuantity"
            label="应检数量"
            width="120"
            align="right"
          />
          <el-table-column
            prop="scannedQuantity"
            label="已扫数量"
            width="120"
            align="right"
          />
        </el-table>
      </el-card>
    </template>

    <el-dialog
      v-if="canEdit"
      title="登记拒单"
      width="520px"
      :visible.sync="rejectionVisible"
      @closed="rejectionReason = ''"
    >
      <el-form label-width="90px">
        <el-form-item label="拒单原因" required>
          <el-input
            v-model.trim="rejectionReason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
            placeholder="请输入拒单原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="rejectionVisible = false">取消</el-button>
        <el-button type="danger" :loading="submitting" @click="submitRejection">
          确认拒单
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import danding from "@/api/danding";
import userApi from "@/api/warehouseReceipt";

export default {
  name: "ReturnQualityInspectionWorkbench",
  data() {
    return {
      ownerReady: false,
      ownerError: "",
      boundCustomerId: "",
      returnMailNo: "",
      scanBarcode: "",
      scanCounts: {},
      inspection: null,
      querying: false,
      submitting: false,
      rejectionVisible: false,
      rejectionReason: ""
    };
  },
  computed: {
    canEdit() {
      return this.ownerReady && !this.boundCustomerId;
    },
    barcodeProgress() {
      if (!this.inspection) {
        return [];
      }
      const progress = {};
      (this.inspection.items || []).forEach(item => {
        const barcode = (item.physicalBarcode || "").trim();
        if (!barcode) {
          return;
        }
        if (!progress[barcode]) {
          progress[barcode] = {
            skuNames: [],
            physicalBarcode: barcode,
            expectedQuantity: 0,
            scannedQuantity: 0,
            unexpected: false
          };
        }
        const expectedQuantity = Number(item.expectedQuantity) || 0;
        progress[barcode].expectedQuantity += expectedQuantity;
        progress[barcode].unexpected =
          progress[barcode].unexpected ||
          (expectedQuantity === 0 && !item.itemNo);
        const skuName = (item.skuName || "").trim();
        if (skuName && progress[barcode].skuNames.indexOf(skuName) === -1) {
          progress[barcode].skuNames.push(skuName);
        }
        if (this.inspection.finalized) {
          progress[barcode].scannedQuantity +=
            Number(item.scannedQuantity) || 0;
        }
      });
      if (!this.inspection.finalized) {
        Object.keys(progress).forEach(barcode => {
          progress[barcode].scannedQuantity = this.scanCounts[barcode] || 0;
        });
        Object.keys(this.scanCounts).forEach(barcode => {
          if (!progress[barcode] && this.scanCounts[barcode] > 0) {
            progress[barcode] = {
              skuNames: [],
              physicalBarcode: barcode,
              expectedQuantity: 0,
              scannedQuantity: this.scanCounts[barcode],
              unexpected: true
            };
          }
        });
      }
      return Object.keys(progress).map(barcode => {
        const item = progress[barcode];
        return {
          skuName: item.unexpected ? "非本单商品" : item.skuNames.join("、"),
          physicalBarcode: item.physicalBarcode,
          expectedQuantity: item.expectedQuantity,
          scannedQuantity: item.scannedQuantity,
          unexpected: item.unexpected
        };
      });
    },
    hasMissingBarcode() {
      return Boolean(
        this.inspection &&
          (this.inspection.items || []).some(
            item => !(item.physicalBarcode || "").trim()
          )
      );
    },
    canComplete() {
      return Boolean(
        this.canEdit &&
          this.inspection &&
          !this.inspection.finalized &&
          this.inspection.relationStatus === "RELATED" &&
          !this.hasMissingBarcode &&
          !this.hasUnexpectedScan &&
          this.barcodeProgress.length &&
          this.barcodeProgress.every(
            item => item.scannedQuantity === item.expectedQuantity
          )
      );
    },
    hasUnexpectedScan() {
      return this.barcodeProgress.some(item => item.unexpected);
    },
    hasOverScan() {
      return this.barcodeProgress.some(
        item => !item.unexpected && item.scannedQuantity > item.expectedQuantity
      );
    },
    statusText() {
      if (!this.inspection.finalized) {
        return this.inspection.relationStatus === "RELATED"
          ? "待质检"
          : "未关联原单";
      }
      return this.inspection.conclusion === "COMPLETED" ? "已完成" : "已拒单";
    },
    statusTagType() {
      if (!this.inspection.finalized) {
        return this.inspection.relationStatus === "RELATED"
          ? "warning"
          : "info";
      }
      return this.inspection.conclusion === "COMPLETED" ? "success" : "danger";
    },
    conclusionText() {
      return this.inspection.conclusion === "COMPLETED" ? "质检完成" : "拒单";
    }
  },
  async mounted() {
    try {
      const user = await userApi.currentUser();
      if (!user || typeof user !== "object")
        throw new Error("用户信息加载失败");
      this.boundCustomerId = (user.customerId || "").trim();
      this.ownerReady = true;
      this.focusReturnMailNo();
    } catch (error) {
      this.ownerError = "货主信息加载失败，请刷新重试";
    }
  },
  methods: {
    async queryInspection() {
      if (!this.ownerReady) return;
      const returnMailNo = this.returnMailNo.trim();
      if (!returnMailNo) {
        this.$message.warning("请输入退回运单号");
        this.focusReturnMailNo();
        return;
      }
      this.querying = true;
      this.inspection = null;
      this.scanCounts = {};
      try {
        this.inspection = await danding.queryReturnQualityInspection({
          returnMailNo
        });
        this.returnMailNo = this.inspection.returnMailNo || returnMailNo;
        if (!this.inspection.finalized) {
          this.focusScanBarcode();
        }
      } finally {
        this.querying = false;
      }
    },
    addScan() {
      if (!this.canEdit) return;
      const barcode = this.scanBarcode.trim();
      if (!barcode) {
        return;
      }
      const progress = this.barcodeProgress.find(
        item => item.physicalBarcode === barcode
      );
      if (!progress || progress.unexpected) {
        this.$set(
          this.scanCounts,
          barcode,
          (this.scanCounts[barcode] || 0) + 1
        );
        this.$message.warning("该商品不属于本单，只能拒单");
        this.scanBarcode = "";
        return;
      }
      const nextQuantity = progress.scannedQuantity + 1;
      this.$set(this.scanCounts, barcode, nextQuantity);
      if (nextQuantity > progress.expectedQuantity) {
        this.$message.warning("该条码已超出应检数量，只能拒单");
      }
      this.scanBarcode = "";
    },
    decreaseScan(barcode) {
      if (!this.canEdit) return;
      const current = this.scanCounts[barcode] || 0;
      if (current > 1) {
        this.$set(this.scanCounts, barcode, current - 1);
      } else if (current === 1) {
        this.$delete(this.scanCounts, barcode);
      }
      this.focusScanBarcode();
    },
    clearScans() {
      if (!this.canEdit) return;
      this.scanCounts = {};
      this.focusScanBarcode();
    },
    submitCompletion() {
      if (!this.canEdit) return;
      this.$confirm("确认当前商品已全部质检完成？", "提示", {
        type: "warning"
      }).then(() => this.submitInspection("COMPLETED", ""));
    },
    openRejection() {
      if (!this.canEdit) return;
      this.rejectionVisible = true;
    },
    submitRejection() {
      if (!this.canEdit) return;
      if (!this.rejectionReason.trim()) {
        this.$message.warning("请输入拒单原因");
        return;
      }
      this.submitInspection("REJECTED", this.rejectionReason.trim());
    },
    async submitInspection(conclusion, rejectionReason) {
      if (!this.canEdit || this.submitting) {
        return;
      }
      this.submitting = true;
      try {
        this.inspection = await danding.submitReturnQualityInspection({
          request: {
            returnMailNo: this.returnMailNo,
            conclusion,
            rejectionReason,
            scans: this.barcodeProgress
              .filter(item => item.scannedQuantity > 0)
              .map(item => ({
                physicalBarcode: item.physicalBarcode,
                quantity: item.scannedQuantity
              }))
          }
        });
        this.rejectionVisible = false;
        this.$message.success(
          conclusion === "COMPLETED" ? "质检已完成" : "拒单已登记"
        );
      } finally {
        this.submitting = false;
      }
    },
    resetWorkbench() {
      this.returnMailNo = "";
      this.scanBarcode = "";
      this.scanCounts = {};
      this.inspection = null;
      this.rejectionVisible = false;
      this.rejectionReason = "";
      this.focusReturnMailNo();
    },
    focusReturnMailNo() {
      this.$nextTick(() => {
        if (this.$refs.returnMailNo) {
          this.$refs.returnMailNo.focus();
        }
      });
    },
    focusScanBarcode() {
      this.$nextTick(() => {
        if (this.$refs.scanBarcode) {
          this.$refs.scanBarcode.focus();
        }
      });
    },
    formatTime(timestamp) {
      if (!timestamp) {
        return "-";
      }
      return new Date(Number(timestamp)).toLocaleString("zh-CN", {
        hour12: false
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.return-quality-inspection {
  padding: 16px;

  .section-card {
    margin-top: 16px;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
  }

  .header-tip,
  .action-tip {
    color: #909399;
    font-size: 13px;
  }

  .record-link {
    margin-left: 16px;
  }

  .info-row {
    line-height: 32px;
  }

  .info-label {
    display: inline-block;
    width: 92px;
    color: #909399;
  }

  .scan-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .action-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
  }

  .danger-tip {
    color: #f56c6c;
    font-size: 13px;
  }
}
</style>
